import { IpaOrFeature } from "../pattern/convertPattern";
import { Pattern } from "../pattern/pattern";
import { SyllablePhonotactics } from "./phonotactics";

// many codas can end with one of these
const extra: Pattern<IpaOrFeature> = { or: ['/s/', '/z/', '/t/', '/d/'] };

/** acceptable phoneme patterns for English syllables */
export const englishPhonotactics: SyllablePhonotactics<IpaOrFeature> = {
  affricates: 'en-aff',
  onset: {
    or: [
      // English patterns (some are from adopted words or have very limited use)
      '/ʃm/', '/ʃn/', '/ʃt/', '/ʃw/',
      '/sfɹ/',
      '/spɫ/', '/skɫ/', '/spɹ/', '/stɹ/', '/skɹ/', '/skw/', '/spj/', '/skj/',
      '/sp/', '/st/', '/sk/', '/sm/', '/sn/', '/sf/',
      '/pɫ/', '/bɫ/', '/kɫ/', '/ɡɫ/', '/pɹ/', '/bɹ/', '/tɹ/', '/dɹ/', '/kɹ/', '/ɡɹ/', '/tw/', '/dw/', '/ɡw/', '/kw/',
      '/pj/', '/bj/', '/kj/', '/ɡj/', '/mj/', '/fj/', '/vj/', '/θj/', '/hj/',
      '/fɫ/', '/sɫ/', '/ʃɫ/', '/fɹ/', '/θɹ/', '/ʃɹ/', '/hw/', '/sw/', '/θw/', '/vw/',
      { and: [':consonant', { not: '/ŋ/' }] },
      '',
    ]
  },
  nucleus: {
    or: [
      // any other pair of vowels makes for separate syllables
      { or: ['/eɪ/', '/aɪ/', '/aʊ/', '/ɔɪ/', '/oʊ/'] },
      // allow accents to use arbitrary diphthongs using the following pattern:
      { seq: [':vowel', ':non-syllabic'] },
      // V
      ':vowel'
    ],
  },
  coda: {
    or: [
      // codas that should only be at the end of a word: 'tθ', 'mt', 'mf', 'dst', 'zm', 'ɫn', 'ŋkst'

      // English patterns
      '/tt͡ʃ/',
      '/dst/', '/ŋst/', '/ɹʒ/', '/ɫft/', '/ɫpd/',
      '/ɫpt͡s/', '/ɫfθs/', // extra
      '/ɫpt/', '/ɫps/', '/ɫfθ/', '/ɫt͡s/', '/ɫst/', '/ɫkt/', '/ɫks/',
      '/ɫbz/', '/ɫd͡z/', '/ɫt͡ʃt/', '/ɫd͡ʒd/', // extra
      '/ɫp/', '/ɫb/', '/ɫt/', '/ɫd/', '/ɫt͡ʃ/', '/ɫd͡ʒ/', '/ɫk/',
      '/ɫfs/', '/ɫvz/', '/ɫvd/', '/ɫθs/', '/ɫs/', // extra
      '/ɫf/', '/ɫv/', '/ɫθ/', '/ɫs/', '/ɫz/', '/ɫʃ/', '/ɫð/',
      '/ɫmz/', '/ɫmd/', '/ɫnz/', // extra
      '/ɫm/', '/ɫn/',
      '/ɹmθ/', '/ɹpt/', '/ɹps/', '/ɹt͡s/', '/ɹst/', '/ɹkt/',
      '/ɹps/', '/ɹpt/', '/ɹbz/', '/ɹbd/', '/ɹt͡s/', '/ɹd͡z/', '/ɹt͡ʃt/', '/ɹd͡ʒd/', '/ɹks/', '/ɹkt/', '/ɹɡz/', // extra
      '/ɹt͡ʃ/', '/ɹd͡ʒ/', '/ɹp/', '/ɹb/', '/ɹt/', '/ɹd/', '/ɹk/', '/ɹɡ/',
      '/ɹfs/', '/ɹft/', '/ɹvz/', '/ɹvd/', '/ɹθs/', '/ɹθt/', '/ɹðz/', '/ɹðd/', '/ɹzd/', '/ɹʃt/', // extra
      '/ɹf/', '/ɹv/', '/ɹθ/', '/ɹð/', '/ɹs/', '/ɹz/', '/ɹʃ/',
      '/ɹmz/', '/ɹmd/', '/ɹnz/', '/ɹnd/', '/ɹnt/', '/ɹɫz/', '/ɹɫd/', // extra
      '/ɹm/', '/ɹn/', '/ɹɫ/',
      '/mpt͡s/', '/mpst/', '/ŋkt͡s/', '/ŋkst/', '/ŋkθs/', // extra
      '/mpt/', '/mps/', '/nt͡s/', '/ŋkt/', '/ŋks/', '/ŋkθ/',
      '/mps/', '/mpt/', '/nt͡s/', '/nd͡z/', '/nt͡ʃt/', '/nd͡ʒd/', '/ŋks/', '/ŋkt/', // extra
      '/mp/', '/mt/', '/nt/', '/nd/', '/nt͡ʃ/', '/nd͡ʒ/', '/ŋk/',
      '/mfs/', '/mft/', '/nθs/', '/nst/', '/nzd/', // extra
      '/mf/', '/mz/', '/nθ/', '/ns/', '/nz/',
      '/ft͡s/', '/sps/', '/spt/', '/st͡s/', '/sks/', '/skt/', '/zmz/', // extra
      '/ft/', '/sp/', '/st/', '/sk/', '/zm/',
      '/fθ/',
      '/ksθs/', '/kst͡s/',  // extra
      '/ksθ/', '/kst/',
      '/pt͡s/', '/kt͡s/', // extra
      '/pt/', '/kt/',
      '/pθs/', '/pst/', '/tθs/', '/t͡st/', '/dθs/', // extra
      '/pθ/', '/ps/', '/tθ/', '/t͡s/', '/dθ/', '/d͡z/', '/ks/',

      // pair (voiced or voiceless) with appropriate endings
      {
        and: [
          { seq: [{ and: [':voiced', { not: { or: ['/w/', '/j/'] } }] }, { or: ['/z/', '/d/'] }] },
          { not: { or: ['/zz/', '/dd/'] } },
        ]
      },
      {
        and: [
          { seq: [{ and: [':voiceless', { not: '/h/' }] }, { or: ['/s/', '/t/'] }] },
          { not: { or: ['/ss/', '/tt/', '/t͡ʃs/'] } },
        ]
      },

      { and: [':consonant', { not: { or: ['/h/', '/w/', '/j/'] } }] },
      '',
    ]
  }
}

/** extended to include foreign words mixed into the en_US dictionary */
export const extendedEnglishPhonotactics: SyllablePhonotactics<IpaOrFeature> = {
  onset: {
    or: [
      '/bɹw/', '/dɹw/',
      '/vɹ/', '/hɹ/', '/sɹ/', '/wɹ/',
      '/bw/', '/fw/', '/nw/', '/jw/',
      '/t͡ʃj/', '/ʃj/', '/d͡ʒj/', '/ɹj/', '/ðj/', '/t͡sj/', '/zj/',
      '/kn/',
      '/vɫ/',
      '/d͡z/',
      '/ʃt/',
      englishPhonotactics.onset
    ]
  },
  nucleus: englishPhonotactics.nucleus,
  coda: {
    or: [
      '/tt͡ʃt/', '/ɫvθ/', '/ŋkd/', '/mpz/',
      '/fsk/', '/ɫsk/', '/ŋɡst/', '/nsk/', '/t͡sk/', '/vsk/',
      '/bst/', '/dst/', '/ɡst/',
      '/bt͡s/', '/dt͡s/', '/gt͡s/', '/ɡt͡s/', '/mt͡s/',
      '/mbz/', '/md͡z/', '/zd͡z/', '/ɫtz/', '/ntz/',
      '/mt͡ʃ/', '/ɹʃt͡ʃ/',
      '/md͡ʒ/', '/td͡ʒ/',
      '/t͡ʃk/',
      '/bʃ/', '/kʃ/', '/nʃ/', '/t͡ʃj/',
      '/d͡ʒɫ/', '/d͡ʒ/',
      '/mpf/', '/mbk/', '/mpk/', '/mpz/',
      '/ndt/', '/nzk/', '/nft/',
      '/bk/', '/fk/', '/ʃk/', '/zk/',
      '/ɫmk/', '/ɫɡ/',
      '/ɹnst/', '/ɹnt͡s/', '/ɹdt͡s/', '/ɹt͡sk/', '/ɹpst/',
      '/ɹɫt/', '/ɹt͡st/', '/ɹzt/', '/ɹdt/', '/ɹpf/', '/ɹsk/', '/ɹʃk/',
      '/ɹŋ/', '/ɹh/',
      '/nð/', '/nf/', '/nk/',
      '/mb/', '/mk/', '/pk/', '/pf/',
      '/ŋɡ/',
      '/θn/', '/θk/',
      '/tɫ/',
      '/tn/',
      '/ɫɹ/', '/nj/', '/tɹ/', '/zɹ/',
      '/wb/',
      '/h/', '/j/', '/w/',
      { seq: [{ and: [':consonant', { not: { or: ['/h/', '/w/', '/j/'] } }] }, extra] },
      englishPhonotactics.coda,
    ]
  },
}


/** these typically use general features rather than specific phonemes */
export const englishFeaturePhonotactics: SyllablePhonotactics<IpaOrFeature> = {
  /**
   * onsets
   */
  onset: {
    or: [
      // plosive plus approximant other than /j/
      // /pl/, /bl/, /kl/, /ɡl/, /pɹ/, /bɹ/, /tɹ/, /dɹ/, /kɹ/, /ɡɹ/, /tw/, /dw/, /ɡw/, /kw/, /pw/
      { seq: [':plosive', { and: [':approximant', { not: '/j/' }] }] },

      // Voiceless fricative or /v/ plus approximant other than /j/
      // /fl/, /sl/, /θl/, /ʃl/, /vl/, /fɹ/, /θɹ/, /ʃɹ/, /hw/, /sw/, /θw/, /vw/
      {
        seq: [
          { or: [{ and: [':voiceless', ':fricative'] }, '/v/'] },
          { and: [':approximant', { not: '/j/' }] }
        ]
      },

      // /s/ plus voiceless non-sibilant fricative plus approximant: /sfɹ/
      '/sfɹ/',
      // /s/ plus voiceless plosive plus approximant
      // /spl/, /skl/, /spɹ/, /stɹ/, /skɹ/, /skw/, /smj/, /spj/, /stj/, /skj/
      { seq: ['/s/', { and: [':voiceless', ':plosive'] }, ':approximant'] },
      // /s/ plus voiceless plosive: /sp/, /st/, /sk/
      { seq: ['/s/', { and: [':voiceless', ':plosive'] }] },
      // /s/ plus nasal other than /ŋ/: /sm/, /sn/
      { seq: ['/s/', { and: [':nasal', { not: '/ŋ/' }] }] },
      // /s/ plus voiceless non-sibilant fricative: /sf/, /sθ/
      { or: ['/sf/', '/sθ/'] },

      // Consonant plus /j/ (before /uː/ or its modified/reduced forms)
      // /pj/, /bj/, /tj/, /dj/, /kj/, /ɡj/, /mj/, /nj/, /fj/, /vj/, /θj/, /sj/, /zj/, /hj/, /lj/
      { seq: [':consonant', '/j/'] },

      // All single consonant phonemes except /ŋ/
      { and: [':consonant', { not: '/ŋ/' }] },

      '', // ∅
    ]
  },


  /**
   * nucleus
   */
  nucleus: {
    or: [
      { seq: [':vowel', ':vowel'] },
      { or: [':vowel', '/m/', '/n/', '/l/', '/ɝ/'] },
    ],
  },


  /**
   * coda - many of these can also end with s, z, t, or d
   */
  coda: {
    or: [
      // Lateral approximant + two consonants: /lpt/, /lps/, /lfθ/, /lts/, /lst/, /lkt/, /lks/
      //{ or: ['/lpts/', '/lfθs/'] },
      //{ or: ['/lpt/', '/lps/', '/lfθ/', '/lts/', '/lst/', '/lkt/', '/lks/'] },
      { or: ['/ɫpts/', '/ɫfθs/'] },
      { or: ['/ɫpt/', '/ɫps/', '/ɫfθ/', '/ɫts/', '/ɫst/', '/ɫkt/', '/ɫks/'] },
      // Lateral approximant plus plosive or affricate: /lp/, /lb/, /lt/, /ld/, /lt͡ʃ/, /ld͡ʒ/, /lk/
      // Lateral approximant + fricative: /lf/, /lv/, /lθ/, /ls/, /lz/, /lʃ/, (/lð/)
      // Lateral approximant + nasal: /lm/, /ln/
      { seq: [':lateral-approximant', ':plosive', extra] },
      { seq: [':lateral-approximant', ':affricate', extra] },
      { seq: [':lateral-approximant', ':fricative', extra] },
      { seq: [':lateral-approximant', ':nasal', extra] },
      { seq: [':lateral-approximant', { or: [':plosive', ':affricate', ':fricative', ':nasal'] }] },

      // In rhotic varieties:
      // /ɹ/ + two consonants: /ɹmθ/, /ɹpt/, /ɹps/, /ɹts/, /ɹst/, /ɹkt/
      { or: ['/ɹpst/', '/ɹtst/'] },
      { or: ['/ɹmθ/', '/ɹpt/', '/ɹps/', '/ɹts/', '/ɹst/', '/ɹkt/'] },
      // /ɹ/ plus plosive or affricate: /ɹp/, /ɹb/, /ɹt/, /ɹd/, /ɹt͡ʃ/, /ɹd͡ʒ/, /ɹk/, /ɹɡ/
      // /ɹ/ + fricative: /ɹf/, /ɹv/, /ɹθ/, /ɹð/, /ɹs/, /ɹz/, /ɹʃ/
      { seq: ['/ɹ/', ':plosive', extra] },
      { seq: ['/ɹ/', ':affricate', extra] },
      { seq: ['/ɹ/', ':fricative', extra] },
      { seq: ['/ɹ/', { or: [':plosive', ':affricate', ':fricative'] }] },
      // /ɹ/ + nasal or lateral: /ɹm/, /ɹn/, /ɹl/
      { seq: ['/ɹ/', ':nasal', extra] },
      { seq: ['/ɹ/', ':lateral-fricative', extra] },
      { seq: ['/ɹ/', ':lateral-approximant', extra] },
      { seq: ['/ɹ/', { or: [':nasal', ':lateral-fricative', ':lateral-approximant'] }] },

      // Nasal + homorganic plosive + plosive or fricative: /mpt/, /mps/, /nts/, /ndθ/, /ŋkt/, /ŋks/, /ŋkθ/
      { or: ['/mpts/', '/mpst/', '/ndθs/', '/ŋkts/', '/ŋkst/', '/ŋkθs/'] },
      { or: ['/mpt/', '/mps/', '/nts/', '/ndθ/', '/ŋkt/', '/ŋks/', '/ŋkθ/'] },
      // Nasal + homorganic plosive or affricate: /mp/, /nt/, /nd/, /nt͡ʃ/, /nd͡ʒ/, /ŋk/
      { or: ['/mps/', '/mpt/', '/nts/', '/nd͡z/', '/nt͡ʃt/', '/nd͡ʒd/', '/ŋks/', '/ŋkt/'] },
      { or: ['/mp/', '/nt/', '/nd/', '/nt͡ʃ/', '/nd͡ʒ/', '/ŋk/'] },
      // Nasal + fricative: /mf/, /mz/, /mθ/, (/nf/), /nθ/, /ns/, /nz/, /ŋθ/
      { seq: [':nasal', ':fricative', extra] },
      { seq: [':nasal', ':fricative'] },

      // Voiceless fricative plus voiceless plosive: /ft/, /sp/, /st/, /sk/
      { or: ['/fts/', '/sps/', '/sts/', '/sks/'] },
      { or: ['/ft/', '/sp/', '/st/', '/sk/'] },
      // Two voiceless fricatives: /fθ/
      '/fθ/',

      // obstruents: /ksθ/, /kst/
      { or: ['/ksθ/', '/ksts/'] },
      { or: ['/ksθ/', '/kst/'] },
      // Two voiceless plosives: /pt/, /kt/
      { or: ['/pts/', '/kts/'] },
      { or: ['/pt/', '/kt/'] },
      // plosive plus fricative: /pθ/, /ps/, /tθ/, /ts/, /dθ/, /d͡z/, /ks/
      { seq: [':plosive', ':fricative', extra] },
      { seq: [':plosive', ':fricative'] },

      // The single consonant phonemes except /h/, /w/, /j/
      { seq: [{ and: [':consonant', { not: { or: ['/h/', '/w/', '/j/'] } }] }, extra] },
      { and: [':consonant', { not: { or: ['/h/', '/w/', '/j/'] } }] },
      // ∅
      '',
    ]
  }
}
